import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Alert,
  useTheme, useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

const API = process.env.REACT_APP_API_URL || '';

const COLORES_MEDICOS = [
  '#1565c0', '#2e7d32', '#ad1457', '#e65100',
  '#6a1b9a', '#00695c', '#4527a0', '#37474f',
  '#558b2f', '#c62828', '#0277bd', '#283593'
];

const ESTADOS = [
  { value: 'pendiente',    label: 'Pendiente',     color: '#1565c0' },
  { value: 'confirmado',   label: 'Confirmado',    color: '#2e7d32' },
  { value: 'en_recepcion', label: 'En recepción',  color: '#6a1b9a' },
  { value: 'demorado',     label: 'Demorado',      color: '#e65100' },
  { value: 'cancelado',    label: 'Cancelado',     color: '#b71c1c' },
  { value: 'atendido',     label: 'Atendido',      color: '#37474f' },
];

function colorPorEstado(estado) {
  return ESTADOS.find(e => e.value === estado)?.color || '#1565c0';
}

const FORM_VACIO = {
  medico_id: '',
  paciente_dni: '',
  paciente_id: null,
  paciente_nombre: '',
  paciente_email: '',
  paciente_telefono: '',
  paciente_obra_social: '',
  paciente_fecha_nacimiento: '',
  paciente_direccion: '',
  motivo: '',
  fecha: '',
  hora_inicio: '09:00',
  duracion: '30',
  estado: 'pendiente',
  notas: '',
  mensaje_paciente: '',
  nota_medico: '',
};

export default function Calendario() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const rolActual = localStorage.getItem('rol') || '';
  const puedeHacerSobreturno = ['admin', 'recepcionista', 'medico'].includes(rolActual);
  const [turnos, setTurnos] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [obrasSociales, setObrasSociales] = useState([]);
  const [medicosVisibles, setMedicosVisibles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modoDialog, setModoDialog] = useState('nuevo');
  const [turnoEditandoId, setTurnoEditandoId] = useState(null);
  const [disponibilidades, setDisponibilidades] = useState({});
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [buscandoDni, setBuscandoDni] = useState(false);
  const [estadoDni, setEstadoDni] = useState(null); // null | 'encontrado' | 'no_encontrado'
  const [error, setError] = useState('');
  const [confirmarSobreturno, setConfirmarSobreturno] = useState(false);
  const [confirmarFueraDeAgenda, setConfirmarFueraDeAgenda] = useState(false);
  const [payloadPendiente, setPayloadPendiente] = useState(null);

  // Ocultar scroll de la página mientras el calendario está montado
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    cargarDatos();
    axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {});
  }, []);

  async function cargarDatos() {
    try {
      const medicosRes = await axios.get(`${API}/api/medicos`);
      const listaMedicos = medicosRes.data;
      setMedicos(listaMedicos);
      setMedicosVisibles(listaMedicos.map(m => m.id));

      const dispPromises = listaMedicos.map(m =>
        axios.get(`${API}/api/medicos/${m.id}/disponibilidad`)
          .then(r => ({ id: m.id, horarios: r.data || [] }))
          .catch(() => ({ id: m.id, horarios: [] }))
      );
      const disps = await Promise.all(dispPromises);
      const dispMap = {};
      disps.forEach(d => { dispMap[d.id] = d.horarios; });
      setDisponibilidades(dispMap);

      const turnosRes = await axios.get(`${API}/api/turnos`);
      setTurnos(turnosRes.data.data || []);
    } catch (err) {
      console.error('Error cargando datos:', err);
    }
  }

  function generarSlotsDisponibilidad() {
    const slots = [];
    const diasSemana = { 'Lunes': 1, 'Martes': 2, 'Miercoles': 3, 'Jueves': 4, 'Viernes': 5, 'Sabado': 6, 'Domingo': 0 };
    const hoy = new Date();
    const inicioSemana = new Date(hoy);
    inicioSemana.setDate(hoy.getDate() - hoy.getDay() + 1);

    medicos.forEach(m => {
      if (!medicosVisibles.includes(m.id)) return;
      const horarios = disponibilidades[m.id] || [];
      horarios.forEach(h => {
        const diaN = diasSemana[h.dia];
        for (let semana = -1; semana <= 4; semana++) {
          const fecha = new Date(inicioSemana);
          fecha.setDate(inicioSemana.getDate() + (diaN - 1) + (semana * 7));
          const fechaStr = fecha.toISOString().split('T')[0];
          slots.push({
            start: `${fechaStr}T${h.desde}`,
            end: `${fechaStr}T${h.hasta}`,
            display: 'background',
            backgroundColor: 'rgba(46, 125, 50, 0.15)',
            groupId: `disp-${m.id}`
          });
        }
      });
    });
    return slots;
  }

  function turnosAEventos() {
    return turnos
      .filter(t => medicosVisibles.includes(t.medico_id))
      .map(t => {
        const medico = medicos.find(m => m.id === t.medico_id);
        const colorIndex = medicos.indexOf(medico) % COLORES_MEDICOS.length;
        const colorMedico = medico?.color || COLORES_MEDICOS[colorIndex];
        const bgColor = colorPorEstado(t.estado);

        const fechaStr = t.fecha ? String(t.fecha).split('T')[0] : '';
        const horaStr = t.hora_inicio ? String(t.hora_inicio).substring(0, 5) : '00:00';

        const [hh, mm] = horaStr.split(':').map(Number);
        const totalMin = hh * 60 + mm + (parseInt(t.duracion) || 30);
        const endHh = String(Math.floor(totalMin / 60)).padStart(2, '0');
        const endMm = String(totalMin % 60).padStart(2, '0');

        const estadoLabel = ESTADOS.find(e => e.value === t.estado)?.label || t.estado;

        const esSobreturno = !!t.sobreturno;
        return {
          id: String(t.id),
          title: `${esSobreturno ? '⚡ ' : ''}${t.paciente_nombre || 'Sin nombre'}`,
          start: `${fechaStr}T${horaStr}`,
          end: `${fechaStr}T${endHh}:${endMm}`,
          backgroundColor: bgColor,
          borderColor: esSobreturno ? '#ff1744' : colorMedico,
          borderWidth: esSobreturno ? 3 : 1,
          extendedProps: { turno: t, medico, estadoLabel, esSobreturno },
        };
      });
  }

  async function buscarPorDni(dni) {
    if (!dni || dni.length < 3) { setEstadoDni(null); return; }
    setBuscandoDni(true);
    try {
      const r = await axios.get(`${API}/api/pacientes/buscar?dni=${encodeURIComponent(dni)}`);
      const p = r.data;
      setForm(f => ({
        ...f,
        paciente_id: p.id,
        paciente_nombre: `${p.nombre} ${p.apellido}`.trim(),
        paciente_email: p.email || '',
        paciente_telefono: p.telefono || '',
        paciente_obra_social: p.obra_social || '',
        paciente_fecha_nacimiento: p.fecha_nacimiento ? String(p.fecha_nacimiento).split('T')[0] : '',
        paciente_direccion: p.direccion || '',
      }));
      setEstadoDni('encontrado');
    } catch {
      setForm(f => ({ ...f, paciente_id: null }));
      setEstadoDni('no_encontrado');
    } finally {
      setBuscandoDni(false);
    }
  }

  function handleDateClick(info) {
    setModoDialog('nuevo');
    setTurnoEditandoId(null);
    setEstadoDni(null);
    const fecha = info.dateStr.split('T')[0];
    const hora = info.dateStr.includes('T') ? info.dateStr.split('T')[1].substring(0, 5) : '09:00';
    setForm({ ...FORM_VACIO, fecha, hora_inicio: hora });
    setError('');
    setDialogOpen(true);
  }

  function handleEventClick(info) {
    if (!info.event.extendedProps.turno) return;
    const t = info.event.extendedProps.turno;
    setModoDialog('editar');
    setTurnoEditandoId(t.id);
    setEstadoDni(t.paciente_dni ? 'encontrado' : null);
    setForm({
      medico_id: t.medico_id,
      paciente_id: t.paciente_id || null,
      paciente_dni: t.paciente_dni || '',
      paciente_nombre: t.paciente_nombre || '',
      paciente_email: t.paciente_email || '',
      paciente_telefono: t.paciente_telefono || '',
      paciente_obra_social: t.paciente_obra_social || '',
      paciente_fecha_nacimiento: '',
      paciente_direccion: '',
      motivo: t.motivo || '',
      fecha: String(t.fecha).split('T')[0],
      hora_inicio: String(t.hora_inicio).substring(0, 5),
      duracion: String(t.duracion || 30),
      estado: t.estado || 'pendiente',
      notas: t.notas || '',
      mensaje_paciente: t.mensaje_paciente || '',
      nota_medico: t.nota_medico || '',
    });
    setError('');
    setDialogOpen(true);
  }

  function handleClose() {
    setDialogOpen(false);
    setTurnoEditandoId(null);
    setEstadoDni(null);
    setForm(FORM_VACIO);
    setError('');
  }

  function toMin(t) { const [h, m] = t.split(':').map(Number); return h * 60 + m; }

  function estaEnAgenda(medicoId, fecha, horaInicio) {
    const horarios = disponibilidades[medicoId] || [];
    if (!horarios.length) return false;
    const diasMap = { 0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miercoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sabado' };
    const diaNombre = diasMap[new Date(fecha + 'T12:00:00').getDay()];
    const bloquesDia = horarios.filter(h => h.dia === diaNombre);
    if (!bloquesDia.length) return false;
    const minHora = toMin(horaInicio);
    return bloquesDia.some(h => minHora >= toMin(h.desde) && minHora < toMin(h.hasta));
  }

  async function guardarTurno() {
    if (!form.medico_id || !form.paciente_nombre || !form.fecha || !form.hora_inicio) {
      setError('Médico, paciente, fecha y hora son obligatorios.');
      return;
    }
    if (form.estado === 'en_recepcion') {
      if (!form.paciente_dni.trim()) { setError('El DNI es obligatorio para pasar a "En recepción".'); return; }
      if (!form.paciente_telefono.trim()) { setError('El teléfono es obligatorio para pasar a "En recepción".'); return; }
      if (!form.paciente_obra_social.trim()) { setError('La obra social es obligatoria para pasar a "En recepción".'); return; }
    }
    setError('');
    const payload = {
      medico_id: form.medico_id,
      paciente_id: form.paciente_id || null,
      paciente_dni: form.paciente_dni || '',
      paciente_nombre: form.paciente_nombre,
      paciente_email: form.paciente_email,
      paciente_telefono: form.paciente_telefono,
      motivo: form.motivo,
      fecha: form.fecha,
      hora_inicio: form.hora_inicio,
      duracion: parseInt(form.duracion) || 30,
      estado: form.estado || 'pendiente',
      notas: form.notas,
      sobreturno: 0,
      mensaje_paciente: form.mensaje_paciente || null,
      nota_medico: form.nota_medico || null,
    };
    // Turno nuevo: verificar si está fuera de la agenda del médico
    if (modoDialog !== 'editar' && puedeHacerSobreturno && !estaEnAgenda(form.medico_id, form.fecha, form.hora_inicio)) {
      setPayloadPendiente(payload);
      setConfirmarFueraDeAgenda(true);
      return;
    }
    await enviarTurno(payload);
  }

  async function enviarTurno(payload) {
    setGuardando(true);
    try {
      if (modoDialog === 'editar') {
        await axios.put(`${API}/api/turnos/${turnoEditandoId}`, payload);
      } else {
        await axios.post(`${API}/api/turnos`, payload);
      }
      if (payload.estado === 'en_recepcion' && form.paciente_id) {
        try {
          await axios.put(`${API}/api/pacientes/${form.paciente_id}`, {
            dni: form.paciente_dni,
            telefono: form.paciente_telefono,
            obra_social: form.paciente_obra_social,
            email: form.paciente_email,
            fecha_nacimiento: form.paciente_fecha_nacimiento || null,
            direccion: form.paciente_direccion,
          });
        } catch {}
      }
      handleClose();
      cargarDatos();
    } catch (err) {
      if (err.response?.status === 409 && puedeHacerSobreturno) {
        setPayloadPendiente(payload);
        setConfirmarSobreturno(true);
      } else {
        setError(err.response?.data?.error || 'Error al guardar el turno.');
      }
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarYGuardarSobreturno() {
    setConfirmarSobreturno(false);
    const p = { ...payloadPendiente, sobreturno: 1 };
    setPayloadPendiente(null);
    setGuardando(true);
    try {
      await axios.post(`${API}/api/turnos`, p);
      handleClose();
      cargarDatos();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el sobreturno.');
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarYGuardarFueraDeAgenda() {
    setConfirmarFueraDeAgenda(false);
    const p = { ...payloadPendiente, sobreturno: 1 };
    setPayloadPendiente(null);
    await enviarTurno(p);
  }

  async function eliminarTurno() {
    setEliminando(true);
    try {
      await axios.delete(`${API}/api/turnos/${turnoEditandoId}`);
      handleClose();
      cargarDatos();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar el turno.');
    } finally {
      setEliminando(false);
    }
  }

  function toggleMedico(id) {
    setMedicosVisibles(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  }

  const DIAS_MAP = { 0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miercoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sabado' };

  function calcularIndicadorDia(dateStr) {
    const diaSemana = DIAS_MAP[new Date(dateStr + 'T12:00:00').getDay()];
    let totalSlots = 0;
    let ocupados = 0;

    medicos.forEach(m => {
      if (!medicosVisibles.includes(m.id)) return;
      const horarios = disponibilidades[m.id] || [];
      horarios.forEach(h => {
        if (h.dia !== diaSemana) return;
        const [dh, dm] = h.desde.split(':').map(Number);
        const [hh, hm] = h.hasta.split(':').map(Number);
        const slots = Math.floor(((hh * 60 + hm) - (dh * 60 + dm)) / 30);
        totalSlots += slots;
      });
      const turnosDia = turnos.filter(t =>
        t.medico_id === m.id &&
        String(t.fecha).split('T')[0] === dateStr &&
        !['cancelado', 'atendido'].includes(t.estado)
      );
      ocupados += turnosDia.length;
    });

    if (totalSlots === 0) return null;
    if (ocupados >= totalSlots) return 'lleno';
    return 'libre';
  }

  function dayCellContent(arg) {
    const dateStr = arg.date.toISOString().split('T')[0];
    const indicador = calcularIndicadorDia(dateStr);
    const color = indicador === 'libre' ? '#66bb6a' : indicador === 'lleno' ? '#ef5350' : null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', px: 0.5 }}>
        <span>{arg.dayNumberText}</span>
        {color && (
          <Box sx={{
            width: 7, height: 7, borderRadius: '50%',
            bgcolor: color, flexShrink: 0, mt: '2px'
          }} />
        )}
      </Box>
    );
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'var(--c-primario)' } },
    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-primario)' },
  };
  const inputStyle = { style: { color: '#e0e0e0' } };
  const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{
          bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 2,
          '& .fc': { color: '#e0e0e0' },
          '& .fc-button': { bgcolor: 'var(--c-primario)', borderColor: 'var(--c-primario)', color: '#e0e0e0' },
          '& .fc-button:hover': { bgcolor: 'var(--c-primario)' },
          '& .fc-col-header-cell': { bgcolor: 'var(--c-primario)' },
          '& td, & th': { borderColor: '#1a2a4a' },
          '& .fc-nuevoTurno-button': {
            bgcolor: 'var(--c-acento) !important',
            borderColor: 'var(--c-acento) !important',
            color: '#000 !important',
            fontWeight: '700 !important',
          },
        }}>
          <FullCalendar
            key={isMobile ? 'mobile' : 'desktop'}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
            locale="es"
            customButtons={{
              nuevoTurno: {
                text: '+ Nuevo Turno',
                click: () => { setForm(FORM_VACIO); setError(''); setDialogOpen(true); },
              }
            }}
            headerToolbar={isMobile ? {
              left: 'prev,next',
              center: 'title',
              right: 'nuevoTurno'
            } : {
              left: 'prev,next today',
              center: 'title',
              right: 'nuevoTurno dayGridMonth,timeGridWeek,timeGridDay'
            }}
            events={[...generarSlotsDisponibilidad(), ...turnosAEventos()]}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            dayCellContent={dayCellContent}
            slotMinTime="07:00:00"
            slotMaxTime="21:00:00"
            slotDuration="00:15:00"
            slotLabelInterval="00:15:00"
            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            displayEventTime={false}
            allDaySlot={false}
            height="calc(100vh - 130px)"
          />
        </Box>
      </Box>

      {/* Panel de médicos */}
      <Box sx={{
        width: { xs: '100%', md: 200 }, flexShrink: 0, bgcolor: 'var(--c-superficie)',
        borderRadius: 2, p: 2, border: '1px solid var(--c-primario)',
        maxHeight: { xs: 'none', md: 'calc(100vh - 130px)' }, overflowY: { xs: 'visible', md: 'auto' },
        order: { xs: -1, md: 0 },
        display: 'flex', flexDirection: { xs: 'row', md: 'column' },
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        gap: { xs: 1, md: 0 },
        alignItems: { xs: 'center', md: 'flex-start' },
      }}>
        <Typography variant="subtitle1" sx={{ color: 'var(--c-acento)', fontWeight: 600, mb: { xs: 0, md: 2 }, mr: { xs: 1, md: 0 }, flexShrink: 0 }}>
          Médicos
        </Typography>
        {medicos.map((m, i) => (
          <Box
            key={m.id}
            onClick={() => toggleMedico(m.id)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: 1, mb: { xs: 0, md: 1 }, borderRadius: 1, cursor: 'pointer',
              bgcolor: medicosVisibles.includes(m.id) ? 'var(--c-primario)' : 'transparent',
              border: '1px solid var(--c-primario)',
              opacity: medicosVisibles.includes(m.id) ? 1 : 0.4
            }}
          >
            <Box sx={{
              width: 12, height: 12, borderRadius: '50%',
              bgcolor: m.color || COLORES_MEDICOS[i % COLORES_MEDICOS.length],
              flexShrink: 0
            }} />
            <Box>
              <Typography sx={{ fontSize: 12, color: '#e0e0e0' }}>{m.nombre}</Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--c-acento)' }}>{m.especialidad}</Typography>
            </Box>
          </Box>
        ))}

        {/* Leyenda disponibilidad */}
        <Typography variant="subtitle2" sx={{ color: 'var(--c-acento)', fontWeight: 600, mt: { xs: 0, md: 2 }, mb: 1, fontSize: 12, flexShrink: 0 }}>
          Disponibilidad
        </Typography>
        {[{ color: '#66bb6a', label: 'Con lugar' }, { color: '#ef5350', label: 'Sin lugar' }].map(d => (
          <Box key={d.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, color: '#b0b0b0' }}>{d.label}</Typography>
          </Box>
        ))}

        {/* Leyenda de estados */}
        <Typography variant="subtitle2" sx={{ color: 'var(--c-acento)', fontWeight: 600, mt: { xs: 0, md: 2 }, mb: 1, fontSize: 12, flexShrink: 0 }}>
          Estados
        </Typography>
        {ESTADOS.map(e => (
          <Box key={e.value} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: e.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, color: '#b0b0b0' }}>{e.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Dialog: horario ya ocupado (sobreturno) */}
      <Dialog open={confirmarSobreturno} onClose={() => setConfirmarSobreturno(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: '#ff1744' }}>⚡ Horario ya ocupado</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Este horario ya tiene un turno asignado para otro paciente.
          </Typography>
          <Typography sx={{ color: '#ffb74d', fontWeight: 600 }}>
            ¿Confirmás el sobreturno? Se agregarán dos pacientes en el mismo horario.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarSobreturno(false)} sx={{ color: 'var(--c-acento)' }}>
            Cancelar
          </Button>
          <Button onClick={confirmarYGuardarSobreturno} variant="contained" disabled={guardando}
            sx={{ bgcolor: '#ff1744', '&:hover': { bgcolor: '#d50000' } }}>
            {guardando ? 'Guardando...' : 'Confirmar sobreturno'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: fuera de agenda del médico */}
      <Dialog open={confirmarFueraDeAgenda} onClose={() => setConfirmarFueraDeAgenda(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: '#ffb74d' }}>📅 Fuera de agenda</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            El profesional no tiene disponibilidad marcada para ese día u horario.
          </Typography>
          <Typography sx={{ color: '#ffb74d', fontWeight: 600 }}>
            ¿Confirmás el turno de todas formas? Quedará registrado como turno excepcional.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarFueraDeAgenda(false)} sx={{ color: 'var(--c-acento)' }}>
            Cancelar
          </Button>
          <Button onClick={confirmarYGuardarFueraDeAgenda} variant="contained" disabled={guardando}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
            {guardando ? 'Guardando...' : 'Confirmar turno excepcional'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog nuevo turno */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle>{modoDialog === 'editar' ? 'Editar Turno' : 'Nuevo Turno'}</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>

          {/* ── Dos columnas ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>

            {/* Columna izquierda — Paciente */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Paciente
              </Typography>
              <Box>
                <TextField fullWidth
                  label="DNI *"
                  value={form.paciente_dni}
                  onChange={(e) => { setForm({ ...form, paciente_dni: e.target.value, paciente_id: null }); setEstadoDni(null); }}
                  onBlur={(e) => buscarPorDni(e.target.value)}
                  InputLabelProps={labelStyle} inputProps={{ ...inputStyle, maxLength: 20 }} sx={fieldSx} />
                {buscandoDni && <Typography sx={{ fontSize: 11, color: 'var(--c-acento)', mt: 0.5 }}>Buscando...</Typography>}
                {estadoDni === 'encontrado' && <Typography sx={{ fontSize: 11, color: '#66bb6a', mt: 0.5 }}>✓ Datos cargados automáticamente</Typography>}
                {estadoDni === 'no_encontrado' && <Typography sx={{ fontSize: 11, color: '#ffb74d', mt: 0.5 }}>Paciente no registrado — completá los datos</Typography>}
              </Box>
              <TextField label="Nombre *" value={form.paciente_nombre}
                onChange={(e) => setForm({ ...form, paciente_nombre: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Email" value={form.paciente_email}
                onChange={(e) => setForm({ ...form, paciente_email: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Teléfono" value={form.paciente_telefono}
                onChange={(e) => setForm({ ...form, paciente_telefono: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Motivo de consulta" value={form.motivo}
                onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Box>

            {/* Columna derecha — Turno */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: 11, color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Turno
              </Typography>
              <FormControl>
                <InputLabel sx={{ color: 'var(--c-acento)' }}>Médico *</InputLabel>
                <Select value={form.medico_id} label="Médico *"
                  onChange={(e) => setForm({ ...form, medico_id: e.target.value })}
                  sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                  {medicos.map(m => <MenuItem key={m.id} value={m.id}>{m.nombre} — {m.especialidad}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Fecha *" type="date" value={form.fecha} fullWidth
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                InputLabelProps={{ shrink: true, style: { color: 'var(--c-acento)', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
                inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="Hora *" type="time" value={form.hora_inicio}
                  onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })}
                  InputLabelProps={{ ...labelStyle, shrink: true }} inputProps={inputStyle} sx={fieldSx} />
                <TextField label="Duración (min)" type="number" value={form.duracion}
                  onChange={(e) => setForm({ ...form, duracion: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={{ ...inputStyle, min: 5, step: 5 }} sx={fieldSx} />
              </Box>
              <FormControl>
                <InputLabel sx={{ color: 'var(--c-acento)' }}>Estado</InputLabel>
                <Select value={form.estado} label="Estado"
                  onChange={(e) => setForm({ ...form, estado: e.target.value })}
                  sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                  {ESTADOS.map(e => (
                    <MenuItem key={e.value} value={e.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: e.color, flexShrink: 0 }} />
                        {e.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Notas internas" value={form.notas} multiline rows={3}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Box>
          </Box>

          {/* ── Datos requeridos para recepción ── */}
          {form.estado === 'en_recepcion' && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '2px solid #42a5f5', bgcolor: 'rgba(66,165,245,0.07)' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#42a5f5', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                📋 Datos requeridos para recepción
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="DNI *" value={form.paciente_dni}
                  onChange={e => setForm({ ...form, paciente_dni: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx}
                  error={!form.paciente_dni.trim()}
                  helperText={!form.paciente_dni.trim() ? 'Requerido' : ''}
                  FormHelperTextProps={{ style: { color: '#ef5350' } }} />
                <TextField label="Teléfono *" value={form.paciente_telefono}
                  onChange={e => setForm({ ...form, paciente_telefono: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx}
                  error={!form.paciente_telefono.trim()}
                  helperText={!form.paciente_telefono.trim() ? 'Requerido' : ''}
                  FormHelperTextProps={{ style: { color: '#ef5350' } }} />
                <FormControl fullWidth sx={fieldSx} error={!form.paciente_obra_social.trim()}>
                  <InputLabel sx={{ color: 'var(--c-acento)' }}>Obra social *</InputLabel>
                  <Select value={form.paciente_obra_social} label="Obra social *"
                    onChange={e => setForm({ ...form, paciente_obra_social: e.target.value })}
                    sx={{ color: '#e0e0e0' }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } } }}>
                    {obrasSociales.map(os => <MenuItem key={os.id} value={os.nombre}>{os.nombre}</MenuItem>)}
                  </Select>
                  {!form.paciente_obra_social.trim() && <Typography sx={{ fontSize: 11, color: '#ef5350', mt: 0.5, ml: 1.5 }}>Requerido</Typography>}
                </FormControl>
                <TextField label="Email" value={form.paciente_email}
                  onChange={e => setForm({ ...form, paciente_email: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
                <TextField label="Fecha de nacimiento" type="date" fullWidth value={form.paciente_fecha_nacimiento}
                  onChange={e => setForm({ ...form, paciente_fecha_nacimiento: e.target.value })}
                  InputLabelProps={{ shrink: true, style: { color: 'var(--c-acento)', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
                  inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
                <TextField label="Dirección" value={form.paciente_direccion}
                  onChange={e => setForm({ ...form, paciente_direccion: e.target.value })}
                  InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              </Box>
            </Box>
          )}

          {/* ── Ancho completo — Mensajes ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {form.mensaje_paciente ? (
              <Box sx={{ p: 1.5, bgcolor: 'rgba(144,202,249,0.08)', borderRadius: 2, borderLeft: '3px solid var(--c-acento)' }}>
                <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 600, mb: 0.5 }}>
                  💬 Mensaje del paciente:
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#e0e0e0' }}>{form.mensaje_paciente}</Typography>
              </Box>
            ) : null}
            <TextField label="📋 Nota para el médico" value={form.nota_medico} multiline rows={2}
              onChange={(e) => setForm({ ...form, nota_medico: e.target.value })}
              placeholder="Indicaciones para el médico antes de la consulta..."
              InputLabelProps={labelStyle} inputProps={inputStyle} sx={{
                ...fieldSx, '& .MuiOutlinedInput-root fieldset': { borderColor: '#66bb6a' },
              }} />
          </Box>


          {error && (
            <Typography sx={{ color: '#f44336', fontSize: 13 }}>{error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {modoDialog === 'editar' && (
            <Button onClick={eliminarTurno} disabled={eliminando}
              sx={{ color: '#f44336', mr: 'auto' }}>
              {eliminando ? 'Eliminando...' : 'Eliminar'}
            </Button>
          )}
          {modoDialog === 'editar' && rolActual === 'medico' && (
            <Button
              startIcon={<MedicalServicesIcon />}
              onClick={() => navigate(`/consulta/${turnoEditandoId}`)}
              sx={{ color: '#81c784', borderColor: '#81c784' }}
              variant="outlined"
            >
              Atender
            </Button>
          )}
          <Button onClick={handleClose} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={guardarTurno} variant="contained" disabled={guardando}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
